import axios from 'axios'
import { azureMapsApiKey } from '@/utils/map/MapHelpers.js'
import { milesBetween } from '@/utils/Distance.js'

const METERS_PER_MILE = 1609.344
// Route Directions is one request per pair against a 50 QPS account limit.
const MAX_CONCURRENT_REQUESTS = 6
const MAX_STRAIGHT_LINE_MILES = 150

// Loaded 5-axle log truck at southeastern legal maximums: 84,000 lb, 13'6" tall, 8'6" wide, ~75 ft.
const TRUCK_SPEC = {
  isVehicleCommercial: true,
  weight: 38102,
  axleWeight: 9072,
  axleCount: 5,
  height: 4.11,
  width: 2.59,
  length: 23.0
}

// Route results cached for the session; null marks a known-unroutable pair so it isn't refetched.
const cache = new Map()

const coordKey = (spot) => `${spot.latitude.toFixed(5)},${spot.longitude.toFixed(5)}`
const pairKey = (origin, destination) => `${coordKey(origin)}|${coordKey(destination)}`

const hasCoordinates = (spot) => typeof spot?.latitude === 'number' && typeof spot?.longitude === 'number'

const waypoint = (spot, pointIndex) => ({
  type: 'Feature',
  geometry: { type: 'Point', coordinates: [spot.longitude, spot.latitude] },
  properties: { pointIndex, pointType: 'waypoint' }
})

async function runThrottled (tasks) {
  const queue = [...tasks]
  const workers = Array.from({ length: Math.min(MAX_CONCURRENT_REQUESTS, queue.length) }, async () => {
    while (queue.length > 0) await queue.shift()()
  })
  await Promise.all(workers)
}

async function fetchRouteMeters (origin, destination) {
  const url = `https://atlas.microsoft.com/route/directions?api-version=2025-01-01&subscription-key=${azureMapsApiKey()}`
  const { data } = await axios.post(url, {
    type: 'FeatureCollection',
    travelMode: 'truck',
    vehicleSpec: TRUCK_SPEC,
    // Log trucks are kept off interstates and state freeways.
    avoid: ['limitedAccessHighways'],
    routeOutputOptions: ['routeSummary'],
    features: [waypoint(origin, 0), waypoint(destination, 1)]
  }, { headers: { 'Content-Type': 'application/geo+json' } })
  const meters = data?.features?.find(f => f.properties?.type === 'RoutePath')?.properties?.distanceInMeters
  return typeof meters === 'number' ? meters : null
}

export async function routeMilesFromOrigin (origin, destinations) {
  if (!hasCoordinates(origin)) return destinations.map(() => undefined)

  const missing = new Map()
  for (const destination of destinations) {
    if (!hasCoordinates(destination)) continue
    if (milesBetween(origin, destination) > MAX_STRAIGHT_LINE_MILES) continue
    const key = pairKey(origin, destination)
    if (!cache.has(key) && !missing.has(key)) missing.set(key, destination)
  }

  await runThrottled([...missing.values()].map(destination => async () => {
    try {
      cache.set(pairKey(origin, destination), await fetchRouteMeters(origin, destination))
    } catch (error) {
      console.error('Truck route request failed', error)
    }
  }))

  return destinations.map(destination => {
    if (!hasCoordinates(destination)) return undefined
    const meters = cache.get(pairKey(origin, destination))
    return typeof meters === 'number' ? meters / METERS_PER_MILE : undefined
  })
}
